import { capitalCase } from 'change-case';

export interface WorkspaceResourceLabelSource {
    editedName?: string | null;
    label?: string | null;
    name?: string | null;
    title?: string | null;
}

const WORKSPACE_RESOURCE_LABEL_KEYS: ReadonlyArray<keyof WorkspaceResourceLabelSource> = [
    'editedName',
    'name',
    'title',
    'label',
];

/** Resolves the canonical display label across normalized and legacy workspace-resource records. */
export const getWorkspaceResourceLabel = (resource?: WorkspaceResourceLabelSource | null, fallback = ''): string => {
    for (const key of WORKSPACE_RESOURCE_LABEL_KEYS) {
        const value = resource?.[key]?.trim();

        if (value) {
            return value;
        }
    }

    return fallback;
};

export type WorkspaceResourceTypeLabelContext = 'auto' | 'dashboard-detail' | 'global' | 'list-detail';

export interface WorkspaceResourceTypeLabelOptions {
    context?: WorkspaceResourceTypeLabelContext;
    pathname?: string;
    plural?: boolean;
}

interface WorkspaceResourceTypeLabels {
    dashboardDetail?: readonly [singular: string, plural: string];
    global: readonly [singular: string, plural: string];
    listDetail?: readonly [singular: string, plural: string];
}

const RESOURCE_TYPE_ALIASES: Readonly<Record<string, string>> = {
    'list-view-columns': 'list-view-column',
    'list-views': 'list-view',
    listviewcolumn: 'list-view-column',
    listviewcolumns: 'list-view-column',
    listview: 'list-view',
    listviews: 'list-view',
    'report-components': 'report-component',
    reportcomponent: 'report-component',
    reportcomponents: 'report-component',
};

const RESOURCE_TYPE_LABELS: Readonly<Record<string, WorkspaceResourceTypeLabels>> = {
    'list-view-column': {
        global: ['List View Column', 'List View Columns'],
        listDetail: ['Column', 'Columns'],
    },
    'list-view': {
        global: ['List View', 'List Views'],
        listDetail: ['View', 'Views'],
    },
    'report-component': {
        dashboardDetail: ['Component', 'Components'],
        global: ['Report Component', 'Report Components'],
    },
};

const normalizeResourceType = (resourceType: string): string => {
    const normalized = resourceType
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase();

    return RESOURCE_TYPE_ALIASES[normalized] ?? normalized;
};

const isResourceDetailPath = (pathname: string, routeSegment: string): boolean => {
    const segments = pathname.split('/').filter(Boolean);
    const routeIndex = segments.lastIndexOf(routeSegment);

    return routeIndex >= 0 && Boolean(segments[routeIndex + 1]);
};

const resolveResourceTypeLabelContext = (
    context: WorkspaceResourceTypeLabelContext,
    pathname: string,
): Exclude<WorkspaceResourceTypeLabelContext, 'auto'> => {
    if (context !== 'auto') {
        return context;
    }

    if (isResourceDetailPath(pathname, 'lists')) {
        return 'list-detail';
    }

    if (isResourceDetailPath(pathname, 'dashboards')) {
        return 'dashboard-detail';
    }

    return 'global';
};

/**
 * Resolves product-language labels for workspace-resource types without leaking
 * API identifiers such as `ListView` or `ReportComponent` into the interface.
 */
export const getWorkspaceResourceTypeLabel = (
    resourceType: string,
    { context = 'auto', pathname, plural = false }: WorkspaceResourceTypeLabelOptions = {},
): string => {
    const normalizedType = normalizeResourceType(resourceType);
    const labels = RESOURCE_TYPE_LABELS[normalizedType];

    if (!labels) {
        const fallback = capitalCase(normalizedType);
        return plural && !fallback.endsWith('s') ? `${fallback}s` : fallback;
    }

    const currentPathname = pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);
    const resolvedContext = resolveResourceTypeLabelContext(context, currentPathname);
    const contextualLabels =
        (resolvedContext === 'list-detail' ? labels.listDetail : undefined) ??
        (resolvedContext === 'dashboard-detail' ? labels.dashboardDetail : undefined) ??
        labels.global;

    return contextualLabels[plural ? 1 : 0];
};
